import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { useAppContext } from '../../../../app/Context';
import { getTimerMetaData, setTimerMetaData } from '../../../../db/timer';

interface TimerContextType {
  countdownStartTime: number;
  setCountdownStartTime: (time: number) => void;
  isTimerRunning: boolean;
  setIsTimerRunning: Dispatch<SetStateAction<boolean>>;
  backgroundImage: string;
  setBackgroundImage: Dispatch<SetStateAction<string>>;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export function useTimerContext() {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('Using useTimerContext outside TimerContextProvider is not allowed.');
  }
  return context;
}

function useTimerContextProviderVM() {
  const { presentationId, activeSlideId } = useAppContext();

  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [countdownStartTime, _setCountdownStartTime] = useState(0);

  useEffect(() => {
    if (!presentationId) return;
    if (!activeSlideId) return;
    const response = getTimerMetaData(presentationId, activeSlideId);
    if (!response) return;
    const { countdownFrom = 0 } = response;
    _setCountdownStartTime(countdownFrom);
  }, [presentationId, activeSlideId]);

  const setCountdownStartTime = (time: number) => {
    if (!presentationId) return;
    if (!activeSlideId) return;

    setTimerMetaData(presentationId, activeSlideId, { ranTill: time }, false);
    _setCountdownStartTime(time);
  };

  return useMemo(
    () => ({
      countdownStartTime,
      setCountdownStartTime,
      isTimerRunning,
      setIsTimerRunning,
      backgroundImage,
      setBackgroundImage,
    }),
    [countdownStartTime, isTimerRunning, backgroundImage]
  );
}

export interface TimerContextProviderProps {
  children: ReactNode;
}

export function TimerContextProvider({ children }: TimerContextProviderProps) {
  const providerValue = useTimerContextProviderVM();
  return <TimerContext.Provider value={providerValue}>{children}</TimerContext.Provider>;
}
